import React, { useState } from 'react';
import { useList } from "@refinedev/core";
import { Card, Col, DatePicker, Row, Select, Statistic, Table, Typography, Button, message, Tabs } from 'antd';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, PieChart, Pie, Cell } from 'recharts';
import dayjs from 'dayjs';
import { FileExcelOutlined, FilePdfOutlined, UserOutlined } from '@ant-design/icons';
import { CHART_COLORS } from '../../utils/chartColors';
import { downloadCsvReport, downloadPdfReport } from '../../utils/reportExport';

const { RangePicker } = DatePicker;
const { Title } = Typography;
const { TabPane } = Tabs;

export const CustomerSalesReport: React.FC = () => {
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().subtract(30, 'days').startOf('day'),
        dayjs().endOf('day')
    ]);
    const [selectedCustomer, setSelectedCustomer] = useState<string | undefined>();

    // Fetch sales data with server-side filtering
    const { data: salesData, isLoading: salesLoading } = useList({
        resource: "sales",
        dataProviderName: "default",
        filters: [
            ...(selectedCustomer ? [{
                field: "customerId",
                operator: "eq",
                value: selectedCustomer,
            }] : []),
            {
                field: "fromDate",
                operator: "eq",
                value: dateRange[0]?.toISOString(),
            },
            {
                field: "toDate",
                operator: "eq",
                value: dateRange[1]?.toISOString(),
            },
        ],
        meta: {
            fields: [
                "id",
                "invoiceNo",
                "transactionDate",
                "customerId",
                "customerName",
                "totalAmount",
                {
                    payments: [
                        "paymentMethod"
                    ]
                },
                {
                    items: [
                        "itemName",
                        "quantity",
                        "unitPrice",
                        "totalAmount"
                    ]
                }
            ],
        },
    });

    // Fetch customers for filter
    const { data: customersData } = useList({
        resource: "customers",
        dataProviderName: "default",
        meta: {
            fields: ["id", "name", "code"],
        },
    });

    // Process data by customer
    const customerSalesMap = (salesData?.data || []).reduce((acc: Record<string, any>, sale) => {
        const customerId = sale.customerId;
        if (!acc[customerId]) {
            acc[customerId] = {
                customerId,
                customerName: sale.customerName,
                totalSales: 0,
                transactionCount: 0,
                transactions: [],
                items: {},
            };
        }

        acc[customerId].totalSales += sale.totalAmount;
        acc[customerId].transactionCount += 1;
        acc[customerId].transactions.push(sale);

        // Process items for this customer
        sale.items?.forEach((item: any) => {
            if (!acc[customerId].items[item.itemName]) {
                acc[customerId].items[item.itemName] = {
                    itemName: item.itemName,
                    quantity: 0,
                    totalAmount: 0,
                };
            }
            acc[customerId].items[item.itemName].quantity += item.quantity;
            acc[customerId].items[item.itemName].totalAmount += item.totalAmount;
        });

        return acc;
    }, {});

    const customerSalesList = Object.values(customerSalesMap).sort(
        (a: any, b: any) => b.totalSales - a.totalSales
    );

    // Top 10 customers for chart
    const topCustomersChart = customerSalesList.slice(0, 10).map((customer: any) => ({
        name: customer.customerName,
        value: customer.totalSales,
    }));

    const totalSalesAll = customerSalesList.reduce((sum: number, c: any) => sum + c.totalSales, 0);
    const totalTransactionsAll = customerSalesList.reduce((sum: number, c: any) => sum + c.transactionCount, 0);
    const avgTransactionValue = totalTransactionsAll > 0 ? totalSalesAll / totalTransactionsAll : 0;
    const exportDateSubtitle = `${dateRange[0].format('YYYY-MM-DD')} – ${dateRange[1].format('YYYY-MM-DD')}`;

    // Customer list columns
    const customerColumns = [
        {
            title: 'Customer',
            dataIndex: 'customerName',
            key: 'customerName',
        },
        {
            title: 'Transactions',
            dataIndex: 'transactionCount',
            key: 'transactionCount',
            sorter: (a: any, b: any) => a.transactionCount - b.transactionCount,
        },
        {
            title: 'Total Sales',
            dataIndex: 'totalSales',
            key: 'totalSales',
            render: (value: number) => `$${value.toFixed(2)}`,
            sorter: (a: any, b: any) => a.totalSales - b.totalSales,
            defaultSortOrder: 'descend',
        },
        {
            title: 'Average Sale',
            key: 'averageSale',
            render: (_, record: any) =>
                `$${(record.totalSales / record.transactionCount).toFixed(2)}`,
            sorter: (a: any, b: any) =>
                (a.totalSales / a.transactionCount) - (b.totalSales / b.transactionCount),
        },
        {
            title: 'Last Transaction',
            key: 'lastTransaction',
            render: (_, record: any) => {
                if (!record.transactions || record.transactions.length === 0) return '-';
                const lastTx = record.transactions[record.transactions.length - 1];
                return lastTx.invoiceNo || '-';
            },
        },
        {
            title: 'Payment Type',
            key: 'paymentType',
            render: (_, record: any) => {
                if (!record.transactions || record.transactions.length === 0) return '-';
                const lastTx = record.transactions[record.transactions.length - 1];
                return lastTx.payments?.[0]?.paymentMethod || '-';
            },
        },
    ];

    // Transaction columns for specific customer
    const transactionColumns = [
        {
            title: 'Reference',
            dataIndex: 'invoiceNo',
            key: 'invoiceNo',
        },
        {
            title: 'Date',
            dataIndex: 'transactionDate',
            key: 'transactionDate',
            render: (value: string) => dayjs(value).format('YYYY-MM-DD HH:mm'),
        },
        {
            title: 'Payment Type',
            key: 'paymentType',
            render: (_: any, record: any) => {
                const payment = record.payments?.[0];
                return payment?.paymentMethod || '-';
            },
        },
        {
            title: 'Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (value: number) => `$${value.toFixed(2)}`,
        },
    ];

    // Items columns for specific customer
    const itemColumns = [
        {
            title: 'Item',
            dataIndex: 'itemName',
            key: 'itemName',
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
        },
        {
            title: 'Total Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (value: number) => `$${value.toFixed(2)}`,
        },
    ];

    // Customer detail view
    const customerDetail = selectedCustomer ? customerSalesMap[selectedCustomer] : null;
    const customerItemsList = customerDetail ? Object.values(customerDetail.items) : [];

    const handleExportExcel = () => {
        if (selectedCustomer && customerDetail) {
            const headers = ['Reference', 'Date', 'Payment Type', 'Amount'];
            const rows = customerDetail.transactions.map((t: any) => [
                t.invoiceNo ?? '',
                dayjs(t.transactionDate).format('YYYY-MM-DD HH:mm'),
                t.payments?.[0]?.paymentMethod ?? '',
                Number(t.totalAmount).toFixed(2),
            ]);
            downloadCsvReport(`customer-${customerDetail.customerName}-transactions`, headers, rows);
        } else {
            const headers = ['Customer', 'Transactions', 'Total Sales', 'Average Sale', 'Last Transaction', 'Payment Type'];
            const rows = customerSalesList.map((c: any) => {
                const lastTx = c.transactions?.length ? c.transactions[c.transactions.length - 1] : null;
                return [
                    c.customerName,
                    c.transactionCount,
                    c.totalSales.toFixed(2),
                    (c.totalSales / c.transactionCount).toFixed(2),
                    lastTx?.invoiceNo ?? '',
                    lastTx?.payments?.[0]?.paymentMethod ?? '',
                ];
            });
            downloadCsvReport('customer-sales-report', headers, rows);
        }
        message.success('Export downloaded (CSV opens in Excel).');
    };

    const handleExportPdf = async () => {
        try {
            if (selectedCustomer && customerDetail) {
                const headers = ['Reference', 'Date', 'Payment', 'Amount'];
                const body = customerDetail.transactions.map((t: any) => [
                    t.invoiceNo ?? '',
                    dayjs(t.transactionDate).format('YYYY-MM-DD HH:mm'),
                    t.payments?.[0]?.paymentMethod ?? '',
                    Number(t.totalAmount).toFixed(2),
                ]);
                await downloadPdfReport(
                    `Customer: ${customerDetail.customerName}`,
                    exportDateSubtitle,
                    headers,
                    body,
                );
            } else {
                const headers = ['Customer', 'Txns', 'Total', 'Avg sale', 'Last ref.', 'Pay.'];
                const body = customerSalesList.map((c: any) => {
                    const lastTx = c.transactions?.length ? c.transactions[c.transactions.length - 1] : null;
                    return [
                        c.customerName,
                        c.transactionCount,
                        c.totalSales.toFixed(2),
                        (c.totalSales / c.transactionCount).toFixed(2),
                        lastTx?.invoiceNo ?? '',
                        lastTx?.payments?.[0]?.paymentMethod ?? '',
                    ];
                });
                await downloadPdfReport('Customer sales report', exportDateSubtitle, headers, body);
            }
            message.success('PDF downloaded.');
        } catch {
            message.error('Could not generate PDF.');
        }
    };

    return (
        <div className="space-y-4">
            <Title level={2}>Customer Sales Report</Title>

            <Card>
                <div className="flex flex-wrap gap-4 mb-4">
                    <RangePicker
                        value={dateRange}
                        onChange={(dates) => dates && setDateRange(dates)}
                        allowClear={false}
                    />
                    <Select
                        style={{ width: 250 }}
                        placeholder="Select Customer"
                        allowClear
                        onChange={setSelectedCustomer}
                        options={customersData?.data?.map((customer: any) => ({
                            value: customer.id,
                            label: `${customer.name} (${customer.code})`,
                        }))}
                        showSearch
                        optionFilterProp="label"
                    />
                    <div style={{ marginLeft: 'auto' }}>
                        <Button icon={<FileExcelOutlined />} style={{ marginRight: '8px' }} onClick={handleExportExcel}>
                            Export to Excel
                        </Button>
                        <Button icon={<FilePdfOutlined />} onClick={() => void handleExportPdf()}>
                            Export to PDF
                        </Button>
                    </div>
                </div>

                <Row gutter={16}>
                    <Col span={8}>
                        <Card>
                            <Statistic
                                title="Total Customers"
                                value={customerSalesList.length}
                                prefix={<UserOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card>
                            <Statistic title="Total Sales" value={totalSalesAll} precision={2} prefix="$" />
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card>
                            <Statistic
                                title="Average transaction value"
                                value={avgTransactionValue}
                                precision={2}
                                prefix="$"
                            />
                        </Card>
                    </Col>
                </Row>
            </Card>

            <Row gutter={16}>
                <Col span={12}>
                    <Card title="Top 10 Customers">
                        <div style={{ height: 300 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={topCustomersChart}
                                    layout="vertical"
                                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" />
                                    <YAxis dataKey="name" type="category" width={150} />
                                    <Tooltip formatter={(value) => [`$${(value as number).toFixed(2)}`, 'Sales']} />
                                    <Bar dataKey="value">
                                        {topCustomersChart.map((_, index) => (
                                            <Cell key={`bar-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="Customer Sales Distribution">
                        <div style={{ height: 300 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={topCustomersChart}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        outerRadius={80}
                                        dataKey="value"
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {topCustomersChart.map((_, index) => (
                                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => [`$${(value as number).toFixed(2)}`, 'Sales']} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </Col>
            </Row>

            {selectedCustomer && customerDetail ? (
                <Card title={`Detail for ${customerDetail.customerName}`}>
                    <Row gutter={16} style={{ marginBottom: '16px' }}>
                        <Col span={8}>
                            <Statistic
                                title="Total Purchases"
                                value={customerDetail.totalSales}
                                precision={2}
                                prefix="$"
                            />
                        </Col>
                        <Col span={8}>
                            <Statistic
                                title="Transactions"
                                value={customerDetail.transactionCount}
                            />
                        </Col>
                        <Col span={8}>
                            <Statistic
                                title="Average Purchase"
                                value={customerDetail.totalSales / customerDetail.transactionCount}
                                precision={2}
                                prefix="$"
                            />
                        </Col>
                    </Row>

                    <Tabs defaultActiveKey="transactions">
                        <TabPane tab="Transactions" key="transactions">
                            <Table
                                columns={transactionColumns}
                                dataSource={customerDetail.transactions}
                                rowKey="id"
                                pagination={{ pageSize: 5 }}
                            />
                        </TabPane>
                        <TabPane tab="Purchased Items" key="items">
                            <Table
                                columns={itemColumns}
                                dataSource={customerItemsList}
                                rowKey="itemName"
                                pagination={{ pageSize: 5 }}
                            />
                        </TabPane>
                    </Tabs>
                </Card>
            ) : (
                <Card title="All Customers">
                    <Table
                        columns={customerColumns}
                        dataSource={customerSalesList}
                        rowKey="customerId"
                        loading={salesLoading}
                        pagination={{ pageSize: 10 }}
                        onRow={(record) => ({
                            onClick: () => setSelectedCustomer(record.customerId),
                            style: { cursor: 'pointer' }
                        })}
                    />
                </Card>
            )}
        </div>
    );
}; 